import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  intelligenceAPI,
  SupplierQualification,
  QualificationTemplate,
  QualificationResponse,
} from '../../lib/api';
import { toast } from 'sonner';
import { useIsCustomer, useIsSupplier } from '../../hooks/usePortalMode';
import { ResponseMap, DialogState, DialogFormState } from './types';
import { EDITABLE_STATUSES } from './constants';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { useCanApproveSuppliers } from '../../hooks/useCanApproveSuppliers';
import { useAutoSave, AutoSaveStatus } from '../../hooks/useAutoSave';
import { toNumericAnswer } from '../../utils/answerValues';

/** Debounce before an in-progress answer is persisted. Long enough that a
 *  part-typed number ("1", "12", "-") settles before it is sent. */
const AUTO_SAVE_DELAY_MS = 3000;

export function useQualificationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isCustomer = useIsCustomer();
  const isSupplier = useIsSupplier();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [qualification, setQualification] = useState<SupplierQualification | null>(null);
  const [template, setTemplate] = useState<QualificationTemplate | null>(null);
  const [responses, setResponses] = useState<ResponseMap>({});
  const [uploadingFiles, setUploadingFiles] = useState<Record<number, boolean>>({});
  const [activeTab, setActiveTab] = useState('overview');

  // CR-6165 auto-save bookkeeping.
  // `editSeqRef` counts local answer edits; `savedSeqRef` records the count as
  // at the last successful save. Comparing the two is what tells us whether
  // there is unsaved work — a plain boolean would be cleared by a save that
  // started before the user's most recent keystroke.
  const editSeqRef = useRef(0);
  // Distinct questions edited since the last successful save — drives the
  // "you have N unsaved changes" prompt, so it counts answers, not keystrokes.
  const dirtyIdsRef = useRef<Set<number>>(new Set());
  const [unsavedCount, setUnsavedCount] = useState(0);
  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  /** Record a local answer edit: arms auto-save and the leave-page guard. */
  const markDirty = useCallback((questionId: number) => {
    editSeqRef.current += 1;
    dirtyIdsRef.current.add(questionId);
    setUnsavedCount(dirtyIdsRef.current.size);
  }, []);

  // Dialog states
  const [dialogs, setDialogs] = useState<DialogState>({
    approve: false,
    reject: false,
    info: false,
  });
  const [dialogForm, setDialogForm] = useState<DialogFormState>({
    approveComments: '',
    rejectReason: '',
    infoRequest: '',
  });
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (id) {
      fetchQualification();
    }
  }, [id, isCustomer]);

  const fetchQualification = async () => {
    setLoading(true);
    // CR-6165: remember how much the user had typed when this fetch started.
    // Refetching replaces `responses` wholesale from the server, so anything
    // typed while the request was in flight would be wiped. With auto-save
    // running, the user is typing far more often during a fetch than before,
    // which turns a rare race into a routine one.
    const editSeqAtFetchStart = editSeqRef.current;
    try {
      const qualRes = await intelligenceAPI.getQualification(parseInt(id!));
      setQualification(qualRes.data);

      if (qualRes.data.template) {
        const templateRes = await intelligenceAPI.getTemplate(qualRes.data.template);
        setTemplate(templateRes.data);
      }

      // The default DRF page size is 20 — qualifications with more answered
      // questions than that (common on full compliance templates) would only
      // get the first 20 responses mapped and the rest of the page rendered
      // as 'no answer'. Walk the ?page= cursor until all pages are mapped.
      const responseMap: ResponseMap = {};
      for (let page = 1; page <= 50; page++) {
        try {
          const resp = await intelligenceAPI.getResponses({ qualification: id, page, page_size: 100 });
          (resp.data.results || []).forEach((r: QualificationResponse) => {
            responseMap[r.question] = r;
          });
          if (!(resp.data as { next?: string | null }).next) break;
        } catch {
          break;
        }
      }
      // Only adopt the server's answers if the user has not typed since this
      // fetch began. Their in-flight edits are newer and will be persisted by
      // the next auto-save; the rest of the page (status, score, completion %)
      // still refreshes above.
      if (editSeqRef.current === editSeqAtFetchStart) {
        setResponses(responseMap);
      }
    } catch (error) {
      console.error('Failed to fetch qualification:', error);
      toast.error('Failed to load qualification');
    } finally {
      setLoading(false);
    }
  };

  const handleResponseChange = (questionId: number, value: string | number | boolean | string[], questionType: string) => {
    // CR-6165: every edit arms the debounced auto-save below.
    markDirty(questionId);
    setResponses((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        question: questionId,
        ...(questionType === 'text' || questionType === 'textarea' ? { text_value: String(value) } : {}),
        ...(questionType === 'number' ? { number_value: toNumericAnswer(value) } : {}),
        ...(questionType === 'date' ? { date_value: String(value) } : {}),
        ...(questionType === 'yes_no' ? { boolean_value: Boolean(value) } : {}),
        ...(questionType === 'single_choice' ? { choice_value: [String(value)] } : {}),
        ...(questionType === 'multiple_choice' ? { choice_value: value as string[] } : {}),
        ...(questionType === 'rating' ? { number_value: toNumericAnswer(value) } : {}),
      },
    }));
  };

  const handleFileUpload = async (questionId: number, file: File) => {
    if (!qualification) return;
    setUploadingFiles((prev) => ({ ...prev, [questionId]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('question_id', String(questionId));
      formData.append('qualification_id', String(qualification.id));

      const res = await intelligenceAPI.uploadResponseFile(formData);
      const { file_path, file_name } = res.data;

      // The server already persisted the file against the response; mirror it
      // into local state so the answer shows immediately and is included in the
      // next Save Draft / Submit payload.
      setResponses((prev) => ({
        ...prev,
        [questionId]: {
          ...prev[questionId],
          question: questionId,
          file_value: file_path,
          file_name,
        },
      }));
      toast.success('File uploaded successfully');
    } catch (error: unknown) {
      console.error('Failed to upload file:', error);
      toast.error(getApiErrorMessage(error, 'Failed to upload file'));
    } finally {
      setUploadingFiles((prev) => ({ ...prev, [questionId]: false }));
    }
  };

  const handleFileDownload = async (questionId: number) => {
    if (!qualification) return;
    try {
      const res = await intelligenceAPI.downloadResponseFile(qualification.id, questionId);
      const blob = res.data as Blob;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = responses[questionId]?.file_name || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error: unknown) {
      console.error('Failed to download file:', error);
      toast.error(getApiErrorMessage(error, 'Failed to download file'));
    }
  };

  const handleFileRemove = (questionId: number) => {
    // Clear locally; the empty file_value persists on the next save. Unlike an
    // upload (which the server has already stored by this point), a removal
    // exists only in local state, so it has to arm auto-save like any edit.
    markDirty(questionId);
    setResponses((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        question: questionId,
        file_value: '',
        file_name: '',
      },
    }));
  };

  /** The whole answer map as the bulk endpoint wants it. */
  const buildResponsesPayload = (map: ResponseMap) =>
    Object.entries(map).map(([questionId, resp]) => ({
      ...resp,
      question: parseInt(questionId),
    }));

  /**
   * CR-6165: the single save path, used by both the manual Save Draft button
   * and the debounced auto-save.
   *
   * `silent` is what separates them. A manual save toasts and refetches so the
   * user sees confirmation and an updated completion %. An auto-save does
   * neither: a toast every few seconds is noise, and the refetch would replace
   * `responses` from the server mid-typing. The status pill is the auto-save's
   * feedback instead.
   */
  const saveResponses = async (map: ResponseMap, { silent }: { silent: boolean }) => {
    if (!qualification) return;
    const seqAtSaveStart = editSeqRef.current;
    // A background save sends only the answers that changed; the manual path
    // still sends the whole map. The endpoint writes one row per entry, so
    // re-posting every answer on each debounce tick churns rows the supplier
    // never touched and scales badly on long questionnaires.
    const scoped = silent
      ? Object.fromEntries(
          Object.entries(map).filter(([questionId]) => dirtyIdsRef.current.has(Number(questionId))),
        )
      : map;
    if (silent && Object.keys(scoped).length === 0) return;
    if (!silent) setSaving(true);
    setAutoSaveStatus('saving');
    try {
      await intelligenceAPI.bulkUpdateResponses(qualification.id, buildResponsesPayload(scoped));
      setAutoSaveStatus('saved');
      setLastSavedAt(new Date());
      // The form is only clean if nothing was typed while the request was in
      // flight. If it was, stay dirty and let the next tick save it — marking
      // clean here would drop those keystrokes from the leave-page guard.
      if (editSeqRef.current === seqAtSaveStart) {
        dirtyIdsRef.current.clear();
        setUnsavedCount(0);
      }
      if (!silent) {
        toast.success('Responses saved successfully');
        fetchQualification();
      }
    } catch (error: unknown) {
      console.error('Failed to save responses:', error);
      setAutoSaveStatus('error');
      // Auto-save failures stay silent in the pill — a toast on every debounce
      // tick while the network is down would bury the page. The manual button
      // still reports properly, because the user asked for that one.
      if (!silent) toast.error(getApiErrorMessage(error, 'Failed to save responses'));
    } finally {
      if (!silent) setSaving(false);
    }
  };

  const handleSaveResponses = async () => {
    await saveResponses(responses, { silent: false });
  };

  const handleSubmit = async (responseNote?: string) => {
    if (!qualification) return;
    setActionLoading(true);
    try {
      // Save responses first
      await intelligenceAPI.bulkUpdateResponses(qualification.id, buildResponsesPayload(responses));

      // Then submit
      await intelligenceAPI.submitQualification(qualification.id, responseNote);
      // Everything is persisted and the record is no longer editable, so drop
      // the dirty state — otherwise the leave-page guard would fire on the way
      // out of a questionnaire the user has just finished.
      dirtyIdsRef.current.clear();
      setUnsavedCount(0);
      toast.success('Qualification submitted for review');
      fetchQualification();
    } catch (error: unknown) {
      console.error('Failed to submit:', error);
      toast.error(getApiErrorMessage(error, 'Failed to submit qualification'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!qualification) return;
    setActionLoading(true);
    try {
      await intelligenceAPI.approveQualification(qualification.id, dialogForm.approveComments);
      toast.success('Qualification approved');
      setDialogs(prev => ({ ...prev, approve: false }));
      setDialogForm(prev => ({ ...prev, approveComments: '' }));
      fetchQualification();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Failed to approve'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!qualification || !dialogForm.rejectReason.trim()) return;
    setActionLoading(true);
    try {
      await intelligenceAPI.rejectQualification(qualification.id, dialogForm.rejectReason);
      toast.success('Qualification rejected');
      setDialogs(prev => ({ ...prev, reject: false }));
      setDialogForm(prev => ({ ...prev, rejectReason: '' }));
      fetchQualification();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Failed to reject'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestInfo = async () => {
    if (!qualification || !dialogForm.infoRequest.trim()) return;
    setActionLoading(true);
    try {
      await intelligenceAPI.requestInfo(qualification.id, dialogForm.infoRequest);
      toast.success('Information request sent');
      setDialogs(prev => ({ ...prev, info: false }));
      setDialogForm(prev => ({ ...prev, infoRequest: '' }));
      fetchQualification();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Failed to send request'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCalculateScore = async () => {
    if (!qualification) return;
    setActionLoading(true);
    try {
      const result = await intelligenceAPI.calculateScore(qualification.id);
      toast.success(`Score calculated: ${result.data.score_percentage}%`);
      fetchQualification();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Failed to calculate score'));
    } finally {
      setActionLoading(false);
    }
  };

  // Permission checks
  // CR-4141 (QA 17/08): being on the customer side is not the same as being
  // allowed to decide. This is the Assessment Overview screen QA reported —
  // Pending Due Diligence > select questionnaire > Review — where a Buyer saw
  // Approve, Reject and Request Info. `isCustomer` alone admitted any customer
  // role, so the same boundary the supplier approve/reject paths enforce is
  // applied here too.
  //
  // Deliberately not applied to canSubmit (the supplier's own action) or
  // canCalculateScore (a reviewer utility that recomputes a score rather than
  // recording a verdict); QA named the three decision controls.
  // Called unconditionally — `isCustomer && useCanApproveSuppliers(...)` would
  // skip the hook whenever isCustomer is false and break the rules of hooks.
  const canApproveSuppliers = useCanApproveSuppliers(isCustomer);
  const canReviewQualification = isCustomer && canApproveSuppliers;
  const canApprove = canReviewQualification && (qualification?.status === 'submitted' || qualification?.status === 'under_review');
  const canReject = canReviewQualification && qualification?.status !== 'approved' && qualification?.status !== 'rejected';
  const canRequestInfo = canReviewQualification && qualification?.status !== 'approved' && qualification?.status !== 'rejected';
  const canSubmit = isSupplier && EDITABLE_STATUSES.includes(qualification?.status || '');
  const canCalculateScore = isCustomer && qualification?.status === 'submitted';

  // CR-6165: debounced auto-save.
  //
  // `enabled` is the important part. It requires:
  //   canSubmit        — supplier, on an editable status. Without it the
  //                      backend 400s on every tick for a read-only
  //                      qualification (_response.py:128) and customers
  //                      viewing an answer sheet would 403.
  //   unsavedCount > 0 — the user has actually edited something. `responses`
  //                      also changes when fetchQualification hydrates it, and
  //                      without this the page would POST on open for every
  //                      visitor.
  //   !saving          — a manual Save Draft is not already in flight.
  //   !actionLoading   — Submit is not running. Submit re-sends the whole map
  //                      itself, so a late auto-save landing after it would be
  //                      a pointless write against a now-submitted record.
  const autoSaveEnabled = canSubmit && unsavedCount > 0 && !saving && !actionLoading;

  useAutoSave(
    responses,
    useCallback(
      async (value: ResponseMap) => { await saveResponses(value, { silent: true }); },
      // saveResponses closes over `qualification` and `responses`; it is
      // recreated each render, and useAutoSave reads it through a ref, so the
      // timer always calls the current one.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [qualification?.id],
    ),
    AUTO_SAVE_DELAY_MS,
    autoSaveEnabled,
  );

  // Browser-level guard for refresh / tab close. Auto-save shrinks the loss
  // window to a few seconds, but those seconds are exactly when someone
  // finishes a last answer and immediately closes the tab.
  useEffect(() => {
    if (unsavedCount <= 0) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Browsers show their own wording; returnValue just has to be set.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [unsavedCount]);

  return {
    // Route params
    id,
    navigate,

    // User info
    isCustomer,
    isSupplier,

    // Loading states
    loading,
    saving,
    actionLoading,

    // CR-6165 auto-save
    autoSaveStatus,
    lastSavedAt,
    unsavedCount,
    hasUnsavedChanges: unsavedCount > 0,

    // Data
    qualification,
    fetchQualification,
    template,
    responses,
    uploadingFiles,
    activeTab,
    setActiveTab,

    // Dialog state
    dialogs,
    setDialogs,
    dialogForm,
    setDialogForm,

    // Permissions
    canApprove,
    canReject,
    canRequestInfo,
    canSubmit,
    canCalculateScore,

    // Actions
    handleResponseChange,
    handleFileUpload,
    handleFileDownload,
    handleFileRemove,
    handleSaveResponses,
    handleSubmit,
    handleApprove,
    handleReject,
    handleRequestInfo,
    handleCalculateScore,
  };
}
